

import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { EMPTY_VALUES, errorMsg } from "../../../../../Apis/functions";
import { TABLES } from "../../../../../Apis/Tables";
import {
    DeleteIconsOptions,
    UpdateIconsOptions,
} from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomTh from "../../../../../components/table/CustomTh";
import { SEARCH_MODEL } from "../../../../../components/table/Model/TableModel";
import TableFunction from "../../../../../components/table/TableFunction";
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import { permissionObj } from "../../../../../Helpers/permission";
import { DeleteItemsApi } from "../../../../../reduxToolkit/Commons/CommonsApi";
import { SimpleSearch } from "../../../../../reduxToolkit/Search/SearchApi";
import { MODAL_NAMES, toggleFinesModal } from "../Model/Model";

/**
 * @Author      Jhovanny Fragoso Márquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * @description
 * Centralized hook that manages all logic for the **Fine Types** view,
 * including table rendering, fetching, pagination, selection, and CRUD actions.
 *
 * This version merges the logic from `FineTypesTable` directly into this file,
 * removing the need for the subcomponent and keeping all functionality cohesive.
 */

const FineTypesApp = () => {
    const dispatch = useDispatch();

    /** Redux states */
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    /** Context hooks */
    const { fineType, setFineType } = useOutletContext();
    const { selectForDelete } = fineType;

    /** Local states */
    const [searchResults, setSearchResults] = useState("");
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [finalArray, setFinalArray] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [search, setSearch] = useState("");
    const [searchElement, setSearchElement] = useState("");
    const [modified, setModified] = useState(false);
    const [tableId] = useState("tbl_ftt_fine_types");
    const [searchId] = useState("txt_ftt_search_fine_types");

    const view = VIEW_TYPE.LIST;
    const option = TABLES.FINE_TYPE;

    /** --- Select / Delete logic --- */
    const handleSelectForDelete = (selected) => {
        setFineType((prev) => ({
            ...prev,
            selectForDelete: selected,
        }));
    };

    /** --- Table Header --- */
    const getThead = () => {
        return [
            <CustomTh key={"name"} tableId={tableId} classes="first_head">
                {t("name")}
            </CustomTh>,
        ];
    };

    /** --- Table Body --- */
    const getTbody = () => {
        if (!searchResults || searchResults?.content?.length === 0)
            return { body: [], data: [] };

        const body = searchResults.content.map((item) => (
            <>
                <CustomTd tableId={tableId} classes="first-column">
                    {item?.name || EMPTY_VALUES.DEFAULT}
                </CustomTd>
            </>
        ));

        return { body, data: searchResults.content };
    };


    /** --- Action buttons --- */
    const getActionsButtons = (item, index) => (
        <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-${item.id}`}
        >
            {(DEV || permission?.includes(permissionObj?.WEB_FINE_TYPE_UPDATE)) && (
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("update")}
                    icon={<UpdateIconsOptions />}
                    click={() => {
                        setFineType((prev) => ({
                            ...prev,
                            name: item.name,
                            id: item.id,
                        }));
                        toggleFinesModal(MODAL_NAMES.EDIT, setFineType);
                    }}
                />
            )}

            {(DEV || permission?.includes(permissionObj?.WEB_FINE_TYPE_DELETE)) && (
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("delete")}
                    fontColor="font-color-error-600"
                    iconColor={"icon-color-error-code-600"}
                    icon={<DeleteIconsOptions />}
                    click={() => {
                        setFineType((prev) => ({
                            ...prev,
                            selectForDelete: [item.id],
                            isDeleteModal: true,
                        }));
                    }}
                />
            )}
        </DropDownMenuProfile>
    );

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            key: searchElement,
            view,
        });

        dispatch(SimpleSearch({ body })).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success === true) {
                    setSearchResults(data);
                }
            }
        );
    };

    /** --- Table core function: pagination, events, etc. --- */
    const {
        t,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        selectedList: selectForDelete,
        setSelectedList: handleSelectForDelete,
        data: searchResults,
        view: view.name,
        labelRowsPerPage: "types_per_page",
        getTbody,
        getThead,
        setIsAllChecked,
        fixedPagination: false,
        hasContainer: true,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });


    /** --- Delete selected items --- */
    const handleDelete = async () => {
        if (!selectForDelete.length) return;

        const { payload: { data } = {} } = await dispatch(DeleteItemsApi({
            tableName: TABLES.FINE_TYPE,
            body: selectForDelete,
        }));

        if (data?.success) {
            setModified(true)
            setIsAllChecked(false);
            handleSelectForDelete([]);
        } else errorMsg(data?.code);

    };


    /** --- Return all states and handlers --- */
    return {
        DEV,
        permission,
        t,
        tableId,
        searchId,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        getActionsButtons,
        pagination,
        isAllChecked,
        setSearchResults,
        setSearchElement,
        option,
        page,
        rowsPerPage,
        setSimpleSearch,
        search,
        setSearch,
        setFinalArray,
        searchElement,
        fineType,
        setFineType,
        handleDelete,
        handleSelectForDelete,
        setIsAllChecked,
        setModified,
    };
};

export default FineTypesApp;
